import React, {
  Children,
  cloneElement,
  CSSProperties,
  PropsWithChildren,
  isValidElement,
  useMemo,
  useCallback,
} from 'react';

import clsx from 'clsx';

import {
  Col,
  ColProps,
  Row,
  RowProps,
  Container,
  FormInputLabels,
  FormInputLabelsProps,
} from '@atoms';

import classes from './radio-group.module.scss';

export type RadioGroupProps = PropsWithChildren<
  {
    flexible?: {
      row: RowProps;
      col: ColProps;
    };
    className?: string;
    style?: CSSProperties;
    onChange?: (value: any) => void;
    disabled?: boolean;
    withoutMargin?: boolean;
    value?: string | number | boolean;
    wideGroup?: boolean;
    boldLabel?: boolean;
    allowUnselect?: boolean;
    valueMap?: Record<string, any>;
  } & FormInputLabelsProps
>;

export const RadioGroup = ({
  label,
  boldLabel,
  flexible,
  className,
  children,
  style,
  value,
  disabled = false,
  onChange,
  withoutMargin,
  wideGroup,
  error,
  touched,
  allowUnselect = false,
  valueMap,
}: RadioGroupProps) => {
  const mappedValue = useMemo(() => {
    if (!value || !valueMap) return value;

    if (typeof value === 'string' && Object.keys(valueMap).includes(value))
      return value;

    const [key] =
      Object.entries(valueMap).find(
        ([_key, optionValue]) =>
          JSON.stringify(value) === JSON.stringify(optionValue),
      ) || [];

    return key;
  }, [value, valueMap]);

  const handleChange = useCallback(
    (value: any) => {
      if (!onChange) return;

      if (valueMap) {
        onChange(valueMap[value]);
      } else {
        onChange(value);
      }
    },
    [valueMap, onChange],
  );

  const radioItems = useMemo(() => {
    const items = Children.map(children, (child) => {
      if (!child || !isValidElement(child)) return null;
      const { value: itemValue, onClick: itemOnClick } = child.props;
      const stringifiedValue =
        typeof itemValue === 'number'
          ? JSON.stringify(itemValue.toString())
          : JSON.stringify(itemValue);

      const checked = stringifiedValue === JSON.stringify(mappedValue);

      const onClick = () => {
        if (itemOnClick) itemOnClick();
        if (disabled) return;
        // in case of custom onChange
        if (onChange) {
          if (checked) {
            if (allowUnselect) handleChange(undefined);
          } else {
            handleChange(itemValue);
          }
        }
      };

      const clonedItem = cloneElement(child, {
        ...child.props,
        checked,
        onClick,
        disabled,
        key: child.props.value,
      });

      if (flexible) {
        return (
          <Col
            {...flexible.col}
            key={child.props.value}
            className={clsx(!withoutMargin && classes.column)}
          >
            {clonedItem}
          </Col>
        );
      }
      return clonedItem;
    });

    if (flexible) {
      return (
        <Container className={classes.container} key={0}>
          <Row {...flexible.row}>{items}</Row>
        </Container>
      );
    }

    return items;
  }, [
    handleChange,
    mappedValue,
    disabled,
    children,
    flexible,
    withoutMargin,
    allowUnselect,
  ]);

  return (
    <FormInputLabels
      className={clsx(classes.group, className, wideGroup && classes.wider)}
      style={style}
      label={label}
      boldLabel={boldLabel}
      error={error}
      touched={touched}
    >
      {radioItems}
    </FormInputLabels>
  );
};
